import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  // API data
  error: null,
  data: null,
  adminRole: [],
  adminsList: [],
  adminDetails: null,
  groupOptions: [],

  // Form data
  formData: {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    adminUsername: '',
    group: '',
    role: null,
    adminId: '',
    permission: {}
  },

  // UI state
  loading: false,
  submitting: false,
  passwordVisible: false,
  selectedGroup: null,

  // Error handling
  errors: {},

  // Success state
  createSuccess: false,
  updateSuccess: false
}

const staffSlice = createSlice({
  name: 'staff',
  initialState,
  reducers: {
    verifyStaffVerificationCodeStart: (state, action) => {
      state.loading = true
      state.error = null
    },
    verifyStaffVerificationCodeSuccess: (state, action) => {
      state.loading = false
      state.error = null
    },
    verifyStaffVerificationCodeFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    // Get All Staff Actions
    getAllStaffStart: (state, action) => {
      state.loading = true
      state.error = null
    },
    getAllStaffSuccess: (state, action) => {
      state.loading = false
      state.data = action.payload
      state.error = null
    },
    getAllStaffFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Get Admin Roles Actions
    getAdminRolesStart: (state, action) => {
      state.loading = true
      state.error = null
    },
    getAdminRolesSuccess: (state, action) => {
      state.loading = false
      state.adminRole = action.payload
      state.error = null
    },
    getAdminRolesFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Get Admins List Actions
    getAdminsListStart: (state, action) => {
      state.loading = true
      state.error = null
    },
    getAdminsListSuccess: (state, action) => {
      state.loading = false
      state.adminsList = action.payload
      state.error = null
    },
    getAdminsListFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Get Admin Details Actions
    getAdminDetailsStart: (state, action) => {
      state.loading = true
      state.error = null
    },
    getAdminDetailsSuccess: (state, action) => {
      state.loading = false
      state.adminDetails = action.payload
      state.error = null
    },
    getAdminDetailsFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Get Staff Groups Actions
    getStaffGroupsStart: (state, action) => {
      state.loading = true
      state.error = null
    },
    getStaffGroupsSuccess: (state, action) => {
      state.loading = false
      const options = action.payload
        .filter(g => g !== "" && g !== null)
        .map(g => ({ label: g, value: g }))
      state.groupOptions = options
      state.error = null
    },
    getStaffGroupsFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Create Staff Admin Actions
    createStaffAdminStart: (state, action) => {
      state.submitting = true
      state.error = null
      state.createSuccess = false
    },
    createStaffAdminSuccess: (state, action) => {
      state.submitting = false
      state.createSuccess = true
      state.formData = initialState.formData
      state.errors = {}
      state.selectedGroup = null
      state.error = null
    },
    createStaffAdminFailure: (state, action) => {
      state.submitting = false
      state.error = action.payload
      state.createSuccess = false
    },

    // Update Staff Status Actions
    updateStaffStatusStart: (state, action) => {
      state.loading = true
      state.error = null
    },
    updateStaffStatusSuccess: (state, action) => {
      state.loading = false
      state.error = null
    },
    updateStaffStatusFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Update Staff Admin Actions
    updateStaffAdminStart: (state, action) => {
      state.submitting = true
      state.error = null
      state.updateSuccess = false
    },
    updateStaffAdminSuccess: (state, action) => {
      state.submitting = false
      state.updateSuccess = true
      state.error = null
      if (state.adminDetails && action.payload) {
        state.adminDetails = { ...state.adminDetails, ...action.payload }
      }
    },
    updateStaffAdminFailure: (state, action) => {
      state.submitting = false
      state.error = action.payload
      state.updateSuccess = false
    },

    // Delete Staff Actions
    deleteStaffStart: (state, action) => {
      state.loading = true
      state.error = null
    },
    deleteStaffSuccess: (state, action) => {
      state.loading = false
      state.error = null
    },
    deleteStaffFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Form Management Actions
    setFormField: (state, action) => {
      const { field, value } = action.payload
      state.formData[field] = value

      // Clear specific field error when user starts typing
      if (state.errors[field]) {
        delete state.errors[field]
      }
    },

    setFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload }
    },

    resetStaffForm: (state) => {
      state.formData = initialState.formData
      state.errors = {}
      state.selectedGroup = null
      state.createSuccess = false
      state.error = null
    },

    setFormErrors: (state, action) => {
      state.errors = action.payload
    },

    clearFormErrors: (state) => {
      state.errors = {}
    },

    // UI Management Actions
    togglePasswordVisibility: (state) => {
      state.passwordVisible = !state.passwordVisible
    },

    setSelectedGroup: (state, action) => {
      state.selectedGroup = action.payload
    },

    addGroupOption: (state, action) => {
      const newOption = action.payload
      if (!state.groupOptions.find(opt => opt.value === newOption.value)) {
        state.groupOptions.push(newOption)
      }
    },

    setLoading: (state, action) => {
      state.loading = action.payload
    },

    setloading: (state, action) => {
      state.loading = action.payload
    }
  }
})

export const {
  verifyStaffVerificationCodeStart,
  verifyStaffVerificationCodeSuccess,
  verifyStaffVerificationCodeFailure,
  // Get All Staff Actions
  getAllStaffStart,
  getAllStaffSuccess,
  getAllStaffFailure,

  // Get Admin Roles Actions
  getAdminRolesStart,
  getAdminRolesSuccess,
  getAdminRolesFailure,

  // Get Admins List Actions
  getAdminsListStart,
  getAdminsListSuccess,
  getAdminsListFailure,

  // Get Admin Details Actions
  getAdminDetailsStart,
  getAdminDetailsSuccess,
  getAdminDetailsFailure,

  // Get Staff Groups Actions
  getStaffGroupsStart,
  getStaffGroupsSuccess,
  getStaffGroupsFailure,

  // Create Staff Admin Actions
  createStaffAdminStart,
  createStaffAdminSuccess,
  createStaffAdminFailure,

  // Update Staff Status Actions
  updateStaffStatusStart,
  updateStaffStatusSuccess,
  updateStaffStatusFailure,

  // Update Staff Admin Actions
  updateStaffAdminStart,
  updateStaffAdminSuccess,
  updateStaffAdminFailure,

  // Delete Staff Actions
  deleteStaffStart,
  deleteStaffSuccess,
  deleteStaffFailure,

  // Form Management Actions
  setFormField,
  setFormData,
  resetStaffForm, // renamed from resetForm
  setFormErrors,
  clearFormErrors,

  // UI Management Actions
  togglePasswordVisibility,
  setSelectedGroup,
  addGroupOption,
  setLoading,
  setloading,
  loading
} = staffSlice.actions

export default staffSlice.reducer
